import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/services/api'
import {
  UserAlert,
  UserAlertListResponse,
  CreateUserAlertPayload,
  UpdateUserAlertPayload,
} from '@/types'
import { toast } from 'sonner'

const CLIENT_ALERTS_QUERY_KEY = 'client-alerts'

export const useClientAlerts = (userId: string) => {
  return useQuery<UserAlertListResponse>({
    queryKey: [CLIENT_ALERTS_QUERY_KEY, userId],
    queryFn: async () => {
      const { data } = await api.get(`/users/${userId}/alerts`)
      return data
    },
    enabled: !!userId,
  })
}

export const useCreateClientAlert = (userId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      payload,
      enforceLimits = true,
    }: {
      payload: CreateUserAlertPayload
      /** false = staff override, bypasses the user's plan gates/limits (card 92). */
      enforceLimits?: boolean
    }) => {
      const { data } = await api.post<UserAlert>(
        `/users/${userId}/alerts?enforce_plan_limits=${enforceLimits}`,
        payload
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CLIENT_ALERTS_QUERY_KEY, userId] })
      queryClient.invalidateQueries({ queryKey: ['users', userId] })
      toast.success('Alerta creada')
    },
  })
}

export const useUpdateClientAlert = (userId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: UpdateUserAlertPayload }) => {
      const { data } = await api.patch<UserAlert>(`/users/${userId}/alerts/${id}`, payload)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CLIENT_ALERTS_QUERY_KEY, userId] })
      queryClient.invalidateQueries({ queryKey: ['users', userId] })
      toast.success('Alerta actualizada')
    },
  })
}

export const useDeleteClientAlert = (userId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/users/${userId}/alerts/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CLIENT_ALERTS_QUERY_KEY, userId] })
      queryClient.invalidateQueries({ queryKey: ['users', userId] })
      toast.success('Alerta eliminada')
    },
  })
}
